import { LoginTypes, confirmUserLogin } from '@api/login';
import { getCompanyDetails, getTokenVerifiedUser } from '@helpers/auth.helper';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
// import mixpanel from 'mixpanel-browser';
import { updateOneSignalDetails } from '@helpers/median.helper';
import useIsMobileApp from '@hooks/useIsMobileApp';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { LoadingDots, showToast } from '@shared/atoms';
import { ToastMessages } from 'constants/toastMessages';
import React, { Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getUserStatusData } from 'store/Actions/UserStatusAction';
import { RootState } from 'store';
import { CompanyDetails } from 'custom';
import styles from './VerifyToken.module.scss';

interface InitialState {
  authenticated: boolean;
  loading: boolean;
}

enum DispatchTypes {
  'SET_AUTHENTICATED',
}

interface Action {
  type: DispatchTypes;
  values: { authenticated: boolean; loading: boolean };
}

const initialState: InitialState = {
  authenticated: false,
  loading: false,
};

const verifyTokenReducer: Reducer<InitialState, Action> = (state, action) => {
  if (action.type === DispatchTypes.SET_AUTHENTICATED) {
    return {
      ...state,
      authenticated: action.values.authenticated,
      loading: action.values.loading,
    };
  }

  return initialState;
};

const VerifyToken: React.FunctionComponent = () => {
  const { userId, token } = useParams<UrlParamsType>();
  const history = useHistory();
  const location = useLocation();
  const [verifyTokenState, dispatchVerifyToken] = useReducer(verifyTokenReducer, initialState);
  const isMobileApp = useIsMobileApp();
  const dispatch = useAppDispatch();
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig.data);

  const [hasVerifiedAuthToken, setHasVerifiedAuthToken] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);
  const redirectPath = localStorage.getItem('redirectPath');
  const isDirectLink = localStorage.getItem('isDirectLink') === 'true';

  const params = new URLSearchParams(location.search);
  const redirectedBy = params.get('redirectedBy');
  const rememberMe = localStorage.getItem('rememberMe');
  const validatedRememberMe = Boolean(rememberMe && rememberMe === 'true') || false;
  const { isDesktop } = useWindowDimensions();

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      const details = await getCompanyDetails();
      if (details) {
        setCompanyDetails(details);
      }
    };

    fetchCompanyDetails();
  }, []);

  const verifyAuthToken = useCallback(async () => {
    dispatchVerifyToken({
      type: DispatchTypes.SET_AUTHENTICATED,
      values: {
        authenticated: false,
        loading: true,
      },
    });

    try {
      const data = await getTokenVerifiedUser(
        token,
        LoginTypes.LINK,
        isMobileApp,
        undefined,
        userId,
        isDirectLink,
        redirectedBy,
        validatedRememberMe,
      );

      dispatchVerifyToken({
        type: DispatchTypes.SET_AUTHENTICATED,
        values: {
          authenticated: true,
          loading: true,
        },
      });

      // mixpanel.identify(data.user.id);
      // mixpanel.track('Log In (Token)');

      await getCompanyDetails();

      dispatch(getUserStatusData());

      await confirmUserLogin({ type: LoginTypes.LINK });

      if (redirectPath) {
        if (redirectPath.charAt(0) === `/`) {
          window.location.href = redirectPath;
        } else {
          window.location.href = `/${redirectPath}`;
        }
      } else {
        window.location.href = '/';
      }

      setTimeout(() => {
        localStorage.removeItem('isDirectLink');
      }, 2000);

      dispatchVerifyToken({
        type: DispatchTypes.SET_AUTHENTICATED,
        values: {
          authenticated: true,
          loading: false,
        },
      });

      await updateOneSignalDetails();

      setHasVerifiedAuthToken(true);
    } catch (err) {
      dispatchVerifyToken({
        type: DispatchTypes.SET_AUTHENTICATED,
        values: {
          authenticated: false,
          loading: false,
        },
      });
      showToast(
        ToastMessages.verifyToken.error.INVALID_AUTHENTICATION,
        {
          autoClose: 1000,
          onClose: async () => {
            history.push('/login?error=invalid');
          },
        },
        'error',
      );
    } finally {
      setHasVerifiedAuthToken(false);
      // localStorage.removeItem('rememberMe');
    }
  }, [isMobileApp, token, userId]);

  useEffect(() => {
    if (!verifyTokenState.authenticated && !hasVerifiedAuthToken) {
      verifyAuthToken();
    }
  }, [hasVerifiedAuthToken, verifyAuthToken, verifyTokenState.authenticated]);

  if (!token || !userId) {
    showToast(ToastMessages.verifyToken.error.INVALID_AUTHENTICATION, undefined, 'error');

    history.push('/login');
  }

  const organizationLogo = companyDetails?.company_logo
    ? `${process.env.PUBLIC_URL}/images/logos/${companyDetails.company_logo}`
    : undefined;
  const organizationAltText = companyDetails?.company_name
    ? `${companyDetails.company_name} logo`
    : undefined;
  const hideHapstarLogoEnabled = companyConfigData?.hideHapstarLogo ?? false;

  return (
    <AuthenticationLayout
      title="Login"
      organizationLogo={organizationLogo}
      organizationAltText={organizationAltText}
      hideHapstarLogoEnabled={hideHapstarLogoEnabled}
    >
      <div className={styles.hsRoot}>
        <LoadingDots size={isDesktop ? 24 : 20} isHapstarLoading dotCount={3} />
      </div>
    </AuthenticationLayout>
  );
};

type UrlParamsType = {
  userId: string;
  token: string;
};

export default VerifyToken;
